/**
 * Inline filter panel presentation for the desktop search surfaces.
 *
 * Renders as a left-side panel inside the modal. This file owns only the
 * chrome — the column itself, the FILTERS / CLEAR ALL header and the fixed
 * Cancel / Show Results footer. The six collapsible sections inside come from
 * the shared `PlayerFilterSections` body (SE-5552), which the other desktop
 * filter surfaces are to adopt in turn so a token, label or behaviour fix
 * stops having to be made in three places.
 * @module components/shared/search-modal/InlineFilterPanel/InlineFilterPanelView
 */

import React, { memo } from "react";

import PlayerFilterSections from "@/components/organisms/shared/PlayerFilterSections";
import type { PlayerFilterSectionsViewProps } from "@/components/organisms/shared/PlayerFilterSections";

import { Stack } from "@/components/atoms/Stack";
import { Pressable } from "@/components/atoms/Pressable";
import { ScrollView } from "@/components/atoms/ScrollView";
import { Text } from "@/components/atoms/Text";

/** Props for InlineFilterPanelView — the shared body's props plus this panel's chrome. */
interface InlineFilterPanelViewProps extends PlayerFilterSectionsViewProps {
  /** Total active filter count */
  readonly activeFilterCount: number;
  /** Clear all filters */
  readonly onClearAll: () => void;

  /* --- Bottom actions --- */
  readonly onCancel: () => void;
  readonly onShowResults: () => void;
}

/**
 * Renders the inline filter panel: chrome around the shared filter sections.
 * @param props - All filter panel props
 */
const InlineFilterPanelView = (props: InlineFilterPanelViewProps) => (
  <Stack className="h-full w-2/5 max-w-md shrink-0 overflow-visible border-r border-outline-default">
    {/* Header: FILTERS + CLEAR ALL */}
    <Stack
      direction="horizontal"
      className="items-center justify-between border-b border-outline-default px-6 py-3"
    >
      <Text variant="title" className="leading-6 text-content-secondary">
        FILTERS
      </Text>
      <Pressable
        testID="player-filter:clear-all"
        onPress={props.onClearAll}
        disabled={props.activeFilterCount === 0}
        className={props.activeFilterCount > 0 ? "opacity-100" : "opacity-30"}
      >
        <Text variant="caption-strong" className="text-accent-primary">
          CLEAR ALL
        </Text>
      </Pressable>
    </Stack>

    {/* Scrollable sections */}
    <ScrollView className="flex-1">
      <PlayerFilterSections {...props} />
    </ScrollView>

    {/* Fixed bottom actions: Cancel + Show Results */}
    <Stack
      direction="horizontal"
      className="flex-row gap-2 bg-surface-base px-4 py-3 shadow-soft-2"
    >
      <Pressable
        onPress={props.onCancel}
        className="web:transition-colors web:duration-150 web:hover:bg-surface-subtle flex-1 items-center justify-center rounded-none border border-outline-strong py-2"
      >
        <Text variant="body-bold" className="text-content-primary">
          Cancel
        </Text>
      </Pressable>
      <Pressable
        testID="player-filter:show-results"
        onPress={props.onShowResults}
        className="web:transition-colors web:duration-150 web:hover:bg-content-secondary flex-1 items-center justify-center rounded-none bg-content-primary py-2"
      >
        <Text variant="body-bold" className="text-content-inverse">
          Show Results
        </Text>
      </Pressable>
    </Stack>
  </Stack>
);

InlineFilterPanelView.displayName = "InlineFilterPanelView";

export default memo(InlineFilterPanelView);
